/**
 * Canonical vendor-identity accessor for everything downstream of OCR.
 *
 * The billing vendor (the entity that issued the invoice and is owed the
 * money) is resolved once, at extraction time, by
 * `resolveInvoiceEntities()` and stored on `invoice_data.extracted.entity_roles`.
 *
 * Any surface that needs "the vendor" — report chapters, external vendor
 * intelligence lookups (Google Places, Secretary of State, BBB, licensing) —
 * MUST go through this module so a customer / vehicle owner / insured /
 * carrier name can never be researched or displayed as the vendor.
 */

import { looksLikeEquipmentDescription, type InvoiceEntityRoles } from "./entity-roles";

type Extracted = Record<string, unknown> | null | undefined;

function str(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/** The persisted entity-role record, when extraction produced one. */
export function readEntityRoles(extracted: Extracted): InvoiceEntityRoles | null {
  const roles = extracted?.entity_roles;
  if (!roles || typeof roles !== "object") return null;
  return roles as InvoiceEntityRoles;
}

/** Names that are explicitly NOT the vendor on this invoice. */
export function nonVendorEntityNames(extracted: Extracted): string[] {
  const roles = readEntityRoles(extracted);
  if (!roles) return [];
  return [
    roles.vehicleOwner?.name,
    roles.customer?.name,
    roles.insured?.name,
    roles.carrier?.name,
    roles.driver?.name,
  ].filter((name): name is string => !!name && name.trim().length > 0);
}

/**
 * Names the classifier proposed as the vendor but could not confirm.
 *
 * When extraction sets `needsVendorConfirmation` and the billing-vendor
 * confidence is `low`, the "vendor" is a guess — on scanned invoices it is
 * frequently a charge description lifted out of the service table. Such a name
 * must never be displayed as the vendor, and must never be sent to external
 * vendor research (Places / SoS / BBB / licensing).
 */
function unconfirmedVendorNames(roles: InvoiceEntityRoles | null): Set<string> {
  if (!roles || roles.needsVendorConfirmation !== true) return new Set();
  const names = [roles.billingVendor, roles.invoiceIssuer, roles.serviceProvider]
    .filter((entity) => entity?.confidence === "low")
    .map((entity) => entity?.name)
    .filter((name): name is string => !!name && name.trim().length > 0);
  return new Set(names.map(normalize));
}

/**
 * Deterministic vendor name for an invoice.
 *
 * Priority:
 *  1. resolved billing vendor from entity-role classification
 *  2. `extracted.vendor_name` / `vendor` — but only when it is not a name the
 *     classifier assigned to a non-vendor role
 *  3. `reviews.vendor`, under the same exclusion rule
 *
 * Returns `null` when the identity is unresolved, so callers render an
 * explicit unverified state instead of an incorrect vendor.
 */
export function resolveVendorName(
  extracted: Extracted,
  reviewVendor?: string | null,
): string | null {
  const roles = readEntityRoles(extracted);
  const unconfirmed = unconfirmedVendorNames(roles);
  const resolved = str(roles?.billingVendor?.name) ?? str(roles?.serviceProvider?.name);
  if (resolved && !looksLikeEquipmentDescription(resolved) && !unconfirmed.has(normalize(resolved))) {
    return resolved;
  }

  const excluded = new Set(nonVendorEntityNames(extracted).map(normalize));
  const candidates = [
    str(extracted?.vendor_name),
    str(extracted?.vendor),
    str(extracted?.supplier_name),
    str(extracted?.supplierName),
    str(reviewVendor),
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const key = normalize(candidate);
    if (excluded.has(key)) continue;
    // A name the classifier could not confirm is not a vendor identity.
    if (unconfirmed.has(key)) continue;
    // A vehicle / equipment / service-line description is never the vendor.
    if (looksLikeEquipmentDescription(candidate)) continue;
    return candidate;
  }
  return null;
}


/** True when the vendor identity is too weak to research externally. */
export function vendorIdentityNeedsConfirmation(extracted: Extracted): boolean {
  const roles = readEntityRoles(extracted);
  if (!roles) return false;
  return roles.needsVendorConfirmation === true;
}

/** Confidence tier for the resolved vendor identity. */
export function vendorIdentityConfidence(
  extracted: Extracted,
): "high" | "medium" | "low" | null {
  const roles = readEntityRoles(extracted);
  return roles?.billingVendor?.confidence ?? null;
}
